import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  FaHtml5, FaCss3Alt, FaJs, FaReact, FaAngular, 
  FaNodeJs, FaPhp, FaWordpress, FaShopify, FaFigma,
  FaLaptopCode, FaShoppingCart, FaCode
} from 'react-icons/fa';
import { 
  SiNextdotjs, SiMongodb, SiMysql, SiWoo, SiStrapi, 
  SiTailwindcss
} from 'react-icons/si';
import { TbBrandReactNative } from 'react-icons/tb';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 15
      }
    }
  };

  const heroVariants = {
    hidden: { scale: 0.92, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const techStack = {
    frontend: [
      { name: 'HTML', icon: FaHtml5, color: '#E34F26' },
      { name: 'CSS', icon: FaCss3Alt, color: '#1572B6' },
      { name: 'JavaScript', icon: FaJs, color: '#F7DF1E' },
      { name: 'React', icon: FaReact, color: '#61DAFB' },
      { name: 'Angular', icon: FaAngular, color: '#DD0031' }
    ],
    backend: [
      { name: 'Node.js', icon: FaNodeJs, color: '#339933' },
      { name: 'Next.js', icon: SiNextdotjs, color: '#000000' },
      { name: 'Koa', icon: FaNodeJs, color: '#33333D' },
      { name: 'PHP', icon: FaPhp, color: '#777BB4' }
    ],
    database: [
      { name: 'MySQL', icon: SiMysql, color: '#4479A1' },
      { name: 'MongoDB', icon: SiMongodb, color: '#47A248' }
    ],
    cms: [
      { name: 'WordPress', icon: FaWordpress, color: '#21759B' },
      { name: 'WooCommerce', icon: SiWoo, color: '#96588A' },
      { name: 'Shopify', icon: FaShopify, color: '#7AB55C' }
    ],
    mobile: [
      { name: 'React Native', icon: TbBrandReactNative, color: '#61DAFB' }
    ],
    design: [
      { name: 'Figma', icon: FaFigma, color: '#F24E1E' }
    ]
  };

  // Flatten all tech stack items for slideshow
  const allTechLogos = [
    ...techStack.frontend,
    ...techStack.backend,
    ...techStack.database,
    ...techStack.cms,
    ...techStack.mobile,
    ...techStack.design
  ];

  // Floating tech icons for hero section
  const floatingIcons = [
    { Icon: FaFigma, color: '#F24E1E', delay: 0, position: { top: '25%', left: '5%' } },
    { Icon: SiStrapi, color: '#2F2E8B', delay: 0.5, position: { top: '45%', left: '2%' } },
    { Icon: SiTailwindcss, color: '#06B6D4', delay: 1, position: { top: '15%', right: '10%' } }
  ];

  const featuredServices = [
    {
      icon: FaLaptopCode,
      title: t('home.service_websites_title'),
      description: t('home.service_websites_desc'),
      features: ['Responsive Design', 'SEO-vriendelijk', 'Snelle Laadtijd'],
      link: '/diensten'
    },
    {
      icon: FaShoppingCart,
      title: t('home.service_webshops_title'),
      description: t('home.service_webshops_desc'),
      features: ['Betalingsintegratie', 'Voorraad Beheer', 'Marketing Tools'],
      link: '/diensten'
    },
    {
      icon: FaCode,
      title: t('home.service_software_title'),
      description: t('home.service_software_desc'),
      features: ['API Integratie', 'Schaalbaar', 'Cloud-based'],
      link: '/diensten'
    }
  ];

  const whyChooseUs = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
        </svg>
      ),
      title: t('home.why_fast_title'),
      description: t('home.why_fast_desc')
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
        </svg>
      ),
      title: t('home.why_quality_title'),
      description: t('home.why_quality_desc')
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="2" fill="currentColor"/>
        </svg>
      ),
      title: t('home.why_custom_title'),
      description: t('home.why_custom_desc')
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: t('home.why_responsive_title'),
      description: t('home.why_responsive_desc')
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: t('home.why_seo_title'),
      description: t('home.why_seo_desc')
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: t('home.why_support_title'),
      description: t('home.why_support_desc')
    }
  ];

  return (
    <div className="home">
      <SEO 
        title="Professionele Websites & Webshops" 
        description="MyWebify bouwt professionele websites en webshops op maat. Wij helpen bedrijven groeien met snelle, veilige en SEO-geoptimaliseerde websites." 
        canonical="/"
      />
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div 
              className="hero-content"
              initial="hidden"
              animate="visible"
              variants={heroVariants}
            >
              <h1>{t('hero.title')}</h1>
              <p className="hero-slogan">
                {t('hero.subtitle')}
              </p>
              <Link to="/contact" className="btn btn-primary btn-large">
                {t('hero.cta')}
              </Link>
            </motion.div>
            
            <motion.div 
              className="hero-image"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Floating Tech Icons */}
              {floatingIcons.map((item, index) => {
                const IconComponent = item.Icon;
                return (
                  <motion.div
                    key={index}
                    className="floating-tech-icon"
                    style={{
                      ...item.position,
                      color: item.color
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: [0, 1, 1],
                      scale: [0, 1.2, 1],
                      y: [0, -15, 0]
                    }}
                    transition={{
                      opacity: { duration: 0.5, delay: 0.5 + item.delay },
                      scale: { duration: 0.6, delay: 0.5 + item.delay },
                      y: {
                        duration: 3 + (item.delay * 0.5),
                        repeat: Infinity,
                        repeatType: 'reverse',
                        ease: 'easeInOut',
                        delay: item.delay
                      }
                    }}
                    whileHover={{ 
                      scale: 1.3,
                      rotate: [0, -10, 10, -10, 0],
                      transition: { duration: 0.5 }
                    }}
                  >
                    <IconComponent />
                  </motion.div>
                );
              })}
              
              {/* Custom SVG Illustration - Modern Dashboard/Devices */}
              <motion.svg 
                viewBox="0 0 600 500" 
                className="hero-svg"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <defs>
                  <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fff7ed" stopOpacity="1" />
                    <stop offset="100%" stopColor="#ffedd5" stopOpacity="1" />
                  </linearGradient>
                  <linearGradient id="screenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="100%" stopColor="#f8fafc" />
                  </linearGradient>
                  <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="10" />
                    <feOffset dx="0" dy="10" result="offsetblur" />
                    <feComponentTransfer>
                      <feFuncA type="linear" slope="0.15" />
                    </feComponentTransfer>
                    <feMerge>
                      <feMergeNode />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <pattern id="dotPattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1" fill="#cbd5e1" opacity="0.5" />
                  </pattern>
                </defs>

                {/* Abstract Background Shapes */}
                <circle cx="300" cy="250" r="240" fill="url(#grad1)" />
                <rect x="0" y="0" width="600" height="500" fill="url(#dotPattern)" opacity="0.3" />

                {/* Main Desktop Window */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  {/* Window Frame */}
                  <rect x="80" y="60" width="440" height="320" rx="12" fill="#ffffff" filter="url(#dropShadow)" />
                  
                  {/* Header Bar */}
                  <path d="M80 72C80 65.3726 85.3726 60 92 60H508C514.627 60 520 65.3726 520 72V90H80V72Z" fill="#f1f5f9" />
                  <circle cx="105" cy="75" r="4" fill="#ef4444" />
                  <circle cx="120" cy="75" r="4" fill="#fbbf24" />
                  <circle cx="135" cy="75" r="4" fill="#10b981" />
                  
                  {/* Screen Content */}
                  <rect x="80" y="90" width="440" height="290" rx="0" fill="url(#screenGrad)" />
                  
                  {/* Mockup UI: Hero */}
                  <rect x="110" y="120" width="200" height="24" rx="4" fill="#1e293b" opacity="0.8" />
                  <rect x="110" y="155" width="260" height="10" rx="2" fill="#94a3b8" opacity="0.4" />
                  <rect x="110" y="175" width="220" height="10" rx="2" fill="#94a3b8" opacity="0.4" />
                  
                  <rect x="110" y="210" width="110" height="36" rx="6" fill="#f97316" />
                  
                  {/* Mockup UI: Image/Graphic on right */}
                  <rect x="350" y="120" width="140" height="120" rx="8" fill="#e2e8f0" />
                  <circle cx="420" cy="180" r="30" fill="#cbd5e1" />
                  
                  {/* Mockup UI: Cards below */}
                  <rect x="110" y="280" width="120" height="70" rx="6" fill="#ffffff" stroke="#e2e8f0" />
                  <rect x="250" y="280" width="120" height="70" rx="6" fill="#ffffff" stroke="#e2e8f0" />
                  <rect x="390" y="280" width="100" height="70" rx="6" fill="#ffffff" stroke="#e2e8f0" />
                </motion.g>

                {/* Floating Code Editor (Glassmorphism) */}
                <motion.g
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  style={{ filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.15))" }}
                >
                  <rect x="360" y="240" width="220" height="160" rx="10" fill="#1e293b" />
                  
                  {/* Header */}
                  <rect x="360" y="240" width="220" height="24" rx="10" fill="#0f172a" />
                  <rect x="360" y="260" width="220" height="10" fill="#1e293b" /> {/* Cover bottom radius of header */}
                  <circle cx="375" cy="252" r="3" fill="#ef4444" />
                  <circle cx="385" cy="252" r="3" fill="#fbbf24" />
                  <circle cx="395" cy="252" r="3" fill="#10b981" />

                  {/* Code Lines */}
                  <rect x="380" y="280" width="80" height="6" rx="2" fill="#f97316" />
                  <rect x="380" y="300" width="160" height="4" rx="2" fill="#64748b" />
                  <rect x="400" y="315" width="120" height="4" rx="2" fill="#38bdf8" />
                  <rect x="400" y="330" width="140" height="4" rx="2" fill="#a78bfa" />
                  <rect x="380" y="350" width="100" height="4" rx="2" fill="#64748b" />
                  <rect x="400" y="365" width="130" height="4" rx="2" fill="#4ade80" />
                  
                  {/* Cursor */}
                  <motion.rect 
                    x="535" y="365" width="2" height="10" fill="#f97316"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                </motion.g>

                {/* Mobile Device */}
                <motion.g
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.1 }}
                >
                  <rect x="60" y="260" width="100" height="190" rx="14" fill="#0f172a" />
                  <rect x="65" y="265" width="90" height="180" rx="10" fill="#ffffff" />
                  
                  {/* Mobile Content */}
                  <rect x="75" y="285" width="70" height="8" rx="2" fill="#f97316" />
                  <rect x="75" y="305" width="50" height="4" rx="1" fill="#94a3b8" />
                  <rect x="75" y="315" width="60" height="4" rx="1" fill="#94a3b8" />
                  <rect x="75" y="335" width="70" height="40" rx="4" fill="#e2e8f0" />
                  <rect x="75" y="390" width="30" height="30" rx="4" fill="#f1f5f9" />
                  <rect x="115" y="390" width="30" height="30" rx="4" fill="#f1f5f9" />
                </motion.g>

                {/* Decorative Elements */}
                <circle cx="540" cy="100" r="6" fill="#38bdf8" opacity="0.6" />
                <circle cx="40" cy="150" r="4" fill="#f97316" opacity="0.4" />

              </motion.svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tech Logos Slideshow */}
      <section className="clients-section">
        <div className="container">
          <p className="clients-subtitle">{t('home.clients_subtitle')}</p>
          <div className="clients-track">
            <div className="clients-slide">
              {[...allTechLogos, ...allTechLogos].map((tech, index) => {
                const IconComponent = tech.icon;
                return (
                  <div key={index} className="client-logo">
                    <div className="tech-logo-icon" style={{ color: tech.color }}>
                      <IconComponent />
                    </div>
                    <span className="client-name">{tech.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="section featured-services">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <h2 className="section-title">{t('home.services_title')}</h2>
            <p className="section-subtitle">
              {t('home.services_subtitle')}
            </p>
            
            <div className="services-grid">
              {featuredServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <motion.div 
                    key={index}
                    className="service-card card"
                    variants={itemVariants}
                    whileHover={{ y: -10 }}
                  >
                    <div className="service-icon">
                      <IconComponent />
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="service-features">
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <span className="checkmark">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={service.link} className="service-link">
                      {t('home.more_info')} →
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Dark Illustration Section */}
      <section className="dark-section section">
        <div className="container">
          <div className="dark-grid">
            <motion.div 
              className="dark-illustration"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <svg viewBox="0 0 600 450" className="animated-drawing" style={{ overflow: 'visible' }}>
                <defs>
                  <linearGradient id="cardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1e293b" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#0f172a" stopOpacity="0.95" />
                  </linearGradient>
                  <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000000" floodOpacity="0.3" />
                  </filter>
                </defs>

                {/* Background Abstract Shapes */}
                <motion.g
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1 }}
                >
                  <circle cx="50" cy="50" r="100" fill="#f97316" opacity="0.05" filter="url(#glow)" />
                  <circle cx="550" cy="400" r="120" fill="#3b82f6" opacity="0.05" filter="url(#glow)" />
                </motion.g>

                {/* Central Hub / Core Idea */}
                <motion.g
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 50, delay: 0.2 }}
                >
                  <circle cx="300" cy="225" r="40" fill="#1e293b" stroke="#f97316" strokeWidth="2" filter="url(#shadow)" />
                  <motion.path 
                    d="M285 225 L315 225 M300 210 L300 240" 
                    stroke="#f97316" strokeWidth="3" strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  />
                </motion.g>

                {/* Orbiting Elements (Reality) */}
                {/* Top Left: Design/Wireframe */}
                <motion.g
                  initial={{ x: 50, y: 50, opacity: 0 }}
                  whileInView={{ x: 0, y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <rect x="100" y="80" width="120" height="80" rx="8" fill="#1e293b" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 4" />
                  <rect x="110" y="95" width="40" height="40" rx="4" stroke="#94a3b8" strokeWidth="1" fill="none" />
                  <line x1="160" y1="100" x2="200" y2="100" stroke="#94a3b8" strokeWidth="1" />
                  <line x1="160" y1="115" x2="190" y2="115" stroke="#94a3b8" strokeWidth="1" />
                  
                  {/* Connector */}
                  <motion.path d="M220 160 L270 200" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 4" opacity="0.3" />
                </motion.g>

                {/* Top Right: Code */}
                <motion.g
                  initial={{ x: -50, y: 50, opacity: 0 }}
                  whileInView={{ x: 0, y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <rect x="380" y="80" width="140" height="90" rx="8" fill="#1e293b" stroke="#3b82f6" strokeWidth="2" filter="url(#shadow)" />
                  <text x="395" y="110" fill="#3b82f6" fontFamily="monospace" fontSize="12" fontWeight="bold">&lt;Dev /&gt;</text>
                  <rect x="395" y="125" width="80" height="6" rx="3" fill="#334155" />
                  <rect x="395" y="140" width="60" height="6" rx="3" fill="#334155" />
                  
                  {/* Connector */}
                  <motion.path d="M380 170 L330 200" stroke="#3b82f6" strokeWidth="1" opacity="0.3" />
                </motion.g>

                {/* Bottom: Final Product */}
                <motion.g
                  initial={{ y: -50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <rect x="150" y="280" width="300" height="160" rx="12" fill="url(#cardGradient)" stroke="#f97316" strokeWidth="2" filter="url(#shadow)" />
                  {/* Browser Header */}
                  <rect x="150" y="280" width="300" height="30" rx="12" fill="#0f172a" />
                  <rect x="150" y="300" width="300" height="10" fill="#0f172a" />
                  <circle cx="170" cy="295" r="4" fill="#ef4444" />
                  <circle cx="185" cy="295" r="4" fill="#fbbf24" />
                  <circle cx="200" cy="295" r="4" fill="#10b981" />
                  
                  {/* Content */}
                  <rect x="170" y="330" width="100" height="60" rx="4" fill="#f97316" opacity="0.8" />
                  <rect x="290" y="330" width="140" height="10" rx="2" fill="#94a3b8" />
                  <rect x="290" y="350" width="120" height="10" rx="2" fill="#94a3b8" opacity="0.6" />
                  <rect x="290" y="370" width="100" height="10" rx="2" fill="#94a3b8" opacity="0.6" />
                  
                  <rect x="170" y="405" width="260" height="20" rx="4" fill="#334155" />

                  {/* Connector */}
                  <motion.path d="M300 265 L300 280" stroke="#f97316" strokeWidth="2" opacity="0.5" />
                </motion.g>

                {/* Floating Particles */}
                <motion.circle cx="100" cy="300" r="4" fill="#f97316" 
                  animate={{ y: [0, -20, 0], opacity: [0.5, 1, 0.5] }} 
                  transition={{ duration: 3, repeat: Infinity }} 
                />
                <motion.circle cx="500" cy="200" r="6" fill="#3b82f6" 
                  animate={{ y: [0, 20, 0], opacity: [0.5, 1, 0.5] }} 
                  transition={{ duration: 4, repeat: Infinity, delay: 1 }} 
                />
              </svg>
            </motion.div>

            <motion.div 
              className="dark-content"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h2>{t('home.idea_title')}</h2>
              <p>
                {t('home.idea_desc1')}
              </p>
              <p>
                {t('home.idea_desc2')}
              </p>
              <Link to="/contact" className="btn btn-primary">
                {t('hero.cta')}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/*           <div className="content-line line-1"></div>
                    <div className="content-line line-2"></div>
                    <div className="content-line line-3"></div>
                    <div className="content-boxes">
                      <div className="small-box"></div>
                      <div className="small-box"></div>
                      <div className="small-box"></div>
                      <div className="small-box"></div>
                      <div className="small-box"></div>
                    </div>
                  </div>
                </div>
                <div className="visual-phone">
                  <div className="phone-content">
                    <div className="phone-box"></div>
                    <div className="phone-line"></div>
                    <div className="phone-line"></div>
                    <div className="phone-grid">
                      <div className="grid-item"></div>
                      <div className="grid-item"></div>
                      <div className="grid-item"></div>
                    </div>
                    <div className="phone-icon icon-1">▶</div>
                    <div className="phone-icon icon-2">👤</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <h2 className="section-title">{t('home.why_title')}</h2>
            <p className="section-subtitle">
              {t('home.why_subtitle')}
            </p>
            
            <div className="why-grid">
              {whyChooseUs.map((item, index) => (
                <motion.div 
                  key={index}
                  className="why-card card"
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                >
                  <motion.div 
                    className="why-icon"
                    whileHover={{ 
                      scale: 1.1,
                      rotate: [0, -5, 5, -5, 0],
                      transition: { duration: 0.5 }
                    }}
                  >
                    {item.icon}
                  </motion.div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <div className="werkwijze-cta">
            <h2>{t('home.cta_title')}</h2>
            <p>{t('home.cta_subtitle')}</p>
            <Link to="/contact" className="btn btn-primary">
              {t('home.cta_button')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
